import { Button, Grid, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { useRef, useState, useCallback, useEffect } from "react";
import docs from "../../img/upload_inverted.svg";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { IExpenseRequest, IFile, PrefilledInfo } from "../../interfaces";
import { dark_blue, light_grey, white } from "../../theme";
import { selectAccount, uploadFilesAction } from "../../redux/UserSlice";
import { ActionButton } from "../ActionButton/ActionButton";
import { selectFiles, setFiles, selectDragOver, setDragOver, removeFile } from "./FileUploaderSlice";
import { useTranslation } from "react-i18next";
import { red } from "../../theme";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { ping } from "../../redux/UserAPI";
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';


const acceptedExtensions: string[] = [".pdf", ".doc", ".docx", ".rtf", ".jpeg", ".jpg"];

const acceptedExtensionsString = acceptedExtensions.join(",");
interface DocumentUploadProps {
  fund: string;
  regNum: number | string;
  disableButton?: boolean;
  expense: IExpenseRequest

}

export function FileUploader(props: DocumentUploadProps) {
  const inputFileRef: any = useRef();
  const { fund, regNum, disableButton, expense } = props;

  const dispatch = useAppDispatch();
  const account = useAppSelector(selectAccount);

  const [err, setErr] = useState<null | string>(null);
  const files = useAppSelector(selectFiles);
  const dragOver = useAppSelector(selectDragOver);

  const width = window.screen.width;
  const longText = ".pdf, .doc, .docx, .png, .jpg, .jpeg";

  useEffect(() => {
    if (files.length) {
      setErr(null);
    }
  }, [files]);

  useEffect(() => {
    window.scroll(0, 0);
    if (!account) {
      dispatch(ping('a'));
    }
  }, []);

  const getKey = useKeyDispensor();

  const handleFilesChosen = (e: any) => {
    if (e.target.files?.length > 0) addFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    dispatch(setDragOver(true));
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    dispatch(setDragOver(true));
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setDragOver(false));
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) addFiles(Array.from(e.dataTransfer.files));
  };

  const addFiles = async (newFiles: File[]) => {
    const withKey: IFile[] = newFiles.map((file) => {
      const [name, extension] = splitName(file.name);

      return {
        file,
        name,
        extension,
        supported: acceptedExtensions.includes(extension.toLocaleLowerCase()),
        empty: file.size === 0,
        key: getKey(),
      };
    });

    const isUnsupported = withKey.find((file) => !file.supported);

    if (isUnsupported) {
      if (err === "files") {
        return dispatch(setFiles(withKey))

      };
      setErr('files')
      return dispatch(setFiles(withKey))
    } else {
      setErr('')
      return dispatch(setFiles(withKey))
    }
  };
 
  const uploadFiles = () => {

    if (expense.REQUEST_DATE && expense.REQUEST_DATE > new Date()) {
      setErr("Date of Expense can not be more than today's date")
      return;
    }
    if (!files || !files.length) {
      setErr("There are no files to upload");
      return;
    }

    const formData: FormData = new FormData();
    if (!files.length) return setErr("Please chose files");
    for (const { file, supported, empty } of files) {
      if (empty || !supported) return setErr("Please remove unsupported/damaged files before uploading.");
      formData.append("files", file);
    }

    const args = {
      formData: formData,
      fund: fund,
      regNum: regNum.toString(),
      expense: { ...expense, REQUEST_AMOUNT_IN_REQUEST_CURRENCY: expense.REQUEST_AMOUNT_IN_REQUEST_CURRENCY ? expense.REQUEST_AMOUNT_IN_REQUEST_CURRENCY.replace(/,/g, '').replace(expense.REQUEST_CURRENCY?.symbol ? expense.REQUEST_CURRENCY?.symbol : '', "") : null }
    };
    dispatch(uploadFilesAction(args));
  };

  return (
    <Grid container justifyContent="center" alignContent={"center"} alignItems="center" >
      <Grid item xs={12} style={{ padding: 0 }}>
        <Typography textAlign="center">Supported files: </Typography>
        <Typography textAlign="center"> {longText} </Typography>
      </Grid>
      <Grid
        item
        textAlign={"center"}
        lg={8}
        md={10}
        xs={12}
        sx={{
          ":hover": { backgroundColor: light_grey, },
          padding: 1,
          backgroundColor: "white",
          borderRadius: '5px'
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
        }}
      >
        <Paper sx={{ border: "1px dashed black", "&:hover": { backgroundColor: light_grey }, padding: '1em', backgroundColor: white, color: "black" }} >
          <input
            accept={acceptedExtensionsString}
            onChange={handleFilesChosen}
            multiple
            type="file"
            hidden
            ref={inputFileRef}
          />

          <Typography variant="h6" sx={{ color: "black", "&:hover": { backgroundColor: light_grey } }}>Drop Files Here</Typography>
          <FileUploadOutlinedIcon />
        </Paper>
      </Grid>

      <Grid container direction="column" justifyContent="center" alignContent="center" alignItems="center" spacing={4}>
        <Grid item md={5} xs={7}>
          {files?.map(({ name, extension, supported, empty, key }, index) => {
            let helperText;
            if (empty) {
              helperText = "empty_file";
            } else if (!supported) {
              helperText = "File type not supported";
            }
            return (
              <Grid key={index} container justifyContent="center" alignContent="center" alignItems="center">
                <AttachFileIcon />
                <Typography component="span" variant="h6">
                  {name || ""}
                  {extension || ""}
                </Typography>
                <IconButton
                  aria-label="close"
                  color="default"
                  size="small"
                  onClick={() => dispatch(removeFile(index))}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </Grid>
            );
          })}
        </Grid>
      </Grid>

      <Grid textAlign={"center"} xs={8} item sx={{ paddingTop: 2 }}>
        <Button component="label" variant="contained" sx={{ width: "100%" }}>
          Browse
          <input
            accept={acceptedExtensionsString}
            onChange={handleFilesChosen}
            multiple
            type="file"
            hidden
            ref={inputFileRef}
          />
        </Button>
      </Grid>
      <Grid textAlign={"center"} xs={8} item sx={{ paddingTop: 2 }}>
        <ActionButton disabled={disableButton} local_error={err} action={uploadFiles} name="Submit"
          tooltip={!expense.REQUEST_DATE || !expense.REQUEST_AMOUNT_IN_REQUEST_CURRENCY
            || parseFloat(expense.REQUEST_AMOUNT_IN_REQUEST_CURRENCY) <= 0 || !expense.GRANT_TYPE || !expense.GRANT_CURRENCY
            || !expense.REQUEST_CURRENCY ? "Fill out all the required fields" : (!files.length ? "Please attach at least one file" : '')} />
      </Grid>
    </Grid>
  );
}

function useKeyDispensor() {
  const dispensor = useRef(0);
  return useCallback(() => dispensor.current++, []);
}

function splitName(name: string) {
  const split = name.split(".");
  const extension = split.length > 1 ? `.${split.pop()}` : "";
  const nameWithoutExtension = split.join(".");

  return [nameWithoutExtension, extension];
}
